import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createEvent } from "../../services/eventService";
import type { CreateEventInput, Event } from "../../types/event";

/**
 * Phase 37 — POST /api/events mutation hook.
 *
 * Variables: { body, idempotencyKey } — caller MUST generate the idempotencyKey
 * via `crypto.randomUUID()` and hold it in `useRef` across automatic retries
 * (RESEARCH Pattern 2). New Save click = new key.
 *
 * On success: predicate-invalidates every cached `['events', ...]` query
 * (C-04 / INTG-05). No optimistic updates (D-03).
 * Toast / analytics are caller-side (onSuccess/onError overrides at form call site).
 */
export function useAddEvent() {
	const qc = useQueryClient();
	return useMutation<
		Event,
		Error,
		{ body: CreateEventInput; idempotencyKey: string }
	>({
		mutationFn: ({ body, idempotencyKey }) => createEvent(body, idempotencyKey),
		onSuccess: () => {
			qc.invalidateQueries({ predicate: (q) => q.queryKey[0] === "events" });
		},
		retry: 0,
	});
}
